/**
 * English message tree — the SOURCE OF TRUTH for this application's keys.
 *
 * `dv.ts` mirrors this file's key shape exactly, and
 * `messages/completeness.spec.ts` fails the build if the two ever drift. Add a
 * key here first, then add it to `dv.ts` in the same position.
 *
 * ---------------------------------------------------------------------------
 * KEY NAMING CONVENTION — every agent adding strings to this app follows it
 * ---------------------------------------------------------------------------
 *
 *   common.*                 Strings used by two or more screens: Save, Cancel,
 *                            Search, "No results". If you are about to write a
 *                            second key for a word that is already in
 *                            `common.*`, use the existing one instead.
 *   nav.groups.<group id>    Sidebar group headings, keyed by `NavGroup.id`
 *                            from `src/router/nav.ts`.
 *   nav.items.<route name>   Sidebar entries, keyed by `NavItem.to` — the ROUTE
 *                            NAME, so a menu label can never drift away from
 *                            the destination it labels.
 *   shell.*                  The application frame: top bar, drawer, site
 *                            switcher, user menu, theme and locale toggles.
 *   roles.<role id>          Role display names, keyed by the role id the API
 *                            sends. The ids are persisted and never renamed;
 *                            only these labels are editorial.
 *   <area>.<screen>.<part>   Everything else. `<area>` is the folder under
 *                            `src/views/`; `<screen>` is a short camelCase name
 *                            for the view, with a list/detail pair nesting as
 *                            `<entity>.list` and `<entity>.detail`.
 *
 *                              views/dashboard/ExecutiveDashboardView.vue
 *                                → dashboard.exec.*
 *                              views/dashboard/OperationsDashboardView.vue
 *                                → dashboard.ops.*
 *                              views/billing/BillListView.vue
 *                                → billing.bills.list.*
 *                              views/billing/BillDetailView.vue
 *                                → billing.bills.detail.*
 *                              views/finance/PayoutRunListView.vue
 *                                → finance.payouts.list.*
 *
 * Rules that go with it:
 *
 *  1. Keys are camelCase, EXCEPT where the key is an identifier somebody else
 *     owns — a route name (`nav.items.device-health`), a role id
 *     (`roles.md_executive`), an API status enum value. Those are copied
 *     verbatim so a rename is a compile-or-test failure, not a silent
 *     mistranslation.
 *  2. One key is one complete sentence or label. NEVER assemble a sentence by
 *     concatenating two keys: word order is not the same in Dhivehi, and the
 *     halves cannot be reordered by the translator. Use `{{tokens}}` instead.
 *  3. Numbers, money, weights and dates are formatted by `@fcms/ui`
 *     (`UiMoney`, `UiWeight`, `formatDateTime`, `formatPercent`, …) and passed
 *     into a key as a token. Never hand-format inside a message, and never
 *     translate a string that a formatter produced.
 *  4. Do not translate: document-number prefixes (FB-, IB-, DB-, PR-), currency
 *     and unit symbols (MVR, laari, kg, kg/h), or the status enum VALUES the
 *     API sends. Term-of-art acronyms MIFCO staff use in English (BML, ERP,
 *     API, CSV, PDF, SSE) stay as they are in both locales.
 *  5. A string only two screens away from being shared belongs in `common.*`.
 *     Five agents inventing five spellings of "No results" is the failure mode
 *     this convention exists to prevent.
 *  6. Every key must exist in `dv.ts` with a non-empty value. Where a Dhivehi
 *     wording is not confidently known, `dv.ts` keeps the ENGLISH string and
 *     the key is listed in that file's header for a native reviewer. An honest
 *     gap is fine; a confident wrong translation on a payout screen is not.
 */
const en = {
  app: {
    name: 'MIFCO FCMS',
    // The mark (apps/ui/brand/) already spells out "Mifco" — this is the
    // quiet system codename shown beside it, not a repeat of the brand.
    systemLabel: 'FCMS',
    subtitle: 'Back office',
    fullName: 'Fish Collection Management System',
  },

  common: {
    loading: 'Loading…',
    saving: 'Saving…',
    save: 'Save',
    saveChanges: 'Save changes',
    cancel: 'Cancel',
    close: 'Close',
    back: 'Back',
    next: 'Next',
    confirm: 'Confirm',
    yes: 'Yes',
    no: 'No',
    ok: 'OK',
    edit: 'Edit',
    delete: 'Delete',
    remove: 'Remove',
    create: 'Create',
    add: 'Add',
    search: 'Search',
    filter: 'Filter',
    applyFilters: 'Apply filters',
    clearFilters: 'Clear filters',
    reset: 'Reset',
    refresh: 'Refresh',
    refreshing: 'Refreshing…',
    retry: 'Try again',
    export: 'Export',
    exportCsv: 'Export CSV',
    download: 'Download',
    print: 'Print',
    copy: 'Copy',
    copied: 'Copied',
    viewAll: 'View all',
    viewDetails: 'View details',
    details: 'Details',
    actions: 'Actions',
    status: 'Status',
    all: 'All',
    none: 'None',
    optional: 'Optional',
    required: 'Required',
    unknown: 'Unknown',
    notAvailable: 'Not available',
    empty: 'Nothing here yet',
    noResults: 'No results',
    somethingWrong: 'Something went wrong',
    loadMore: 'Load more',
    dateFrom: 'From',
    dateTo: 'To',
    site: 'Collection point',
    /** The terse form of `site` for a table column, filter chip, or a confirm-dialog summary line. */
    siteChip: 'Site',
    allSites: 'All sites',
    vessel: 'Vessel',
    fisherman: 'Fisherman',
    /** The three kinds of goods MIFCO bills for. Shared by the bill-type and rate-kind pickers. */
    fish: 'Fish',
    ice: 'Ice',
    diesel: 'Diesel',
    fishType: 'Fish type',
    amount: 'Amount',
    rate: 'Rate',
    weight: 'Weight',
    netWeight: 'Net weight',
    total: 'Total',
    notes: 'Notes',
    reference: 'Reference',
    createdAt: 'Created',
    updatedAt: 'Updated',
    issuedAt: 'Issued',
    dash: '—',
    kg: '{{value}} kg',
    never: 'never',
    pcs: 'Pcs',
    gross: 'Gross',
    net: 'Net',
    captured: 'Captured',
    device: 'Device',
    app: 'App',
    scale: 'Scale',
    lastSeen: 'Last seen',
    lastAddress: 'Last address',
    registered: 'Registered',
    session: 'Session',
    revoke: 'Revoke',
    revokeDevice: 'Revoke device',
    revokeConfirmTitle: 'Revoke this device?',
    deviceRevoked: 'Device revoked',
    deviceName: 'Device name',
    value: 'Value',
    entries: 'Entries',
    grade: 'Grade',
    source: 'Source',
    description: 'Description',
    memo: 'Memo',
    account: 'Account',
    kind: 'Kind',
    dr: 'Dr',
    cr: 'Cr',
    drCr: 'Dr/Cr',
    debits: 'Debits',
    credits: 'Credits',
    amountLaari: '{{value}} laari',
    /** A record's plain name, as a table column header. See also `nameEn`/`nameDv`. */
    name: 'Name',
    nameEn: 'Name (English)',
    nameDv: 'Name (Dhivehi)',
    code: 'Code',
    island: 'Island',
    atoll: 'Atoll',
    /** The "is this record active" switch label, shared by every master-data form. */
    active: 'Active',
    partner: 'Partner',
    legacyRef: 'Masveriya reference',
    /** `<UiChart timeAxis>`'s X-series legend label. Never hand-format inside
     *  this string — it is a bare axis label, not a sentence. */
    chartTimeAxis: 'Time',
  },

  nav: {
    /** Keyed by `NavGroup.id` in src/router/nav.ts. */
    groups: {
      overview: 'Overview',
      collection: 'Collection',
      devices: 'Scales & devices',
      billing: 'Billing',
      finance: 'Finance',
      sports: 'Sports fishing',
      reports: 'Reports',
      integrations: 'Integrations',
      masterdata: 'Master data',
      settings: 'Settings',
    },
    /**
     * Keyed by `NavItem.to` — the route name. The English values here must
     * match the `label` in src/router/nav.ts; tests/navigation.spec.ts asserts
     * the rendered English labels, so a drift fails there.
     */
    items: {
      'dashboard-executive': 'Executive dashboard',
      'dashboard-operations': 'Operations',
      'assistant-brief': 'Daily brief',
      sessions: 'Sessions',
      'manual-entry': 'Manual entry',
      queue: 'Queue board',
      devices: 'Device registry',
      'device-health': 'Fleet health',
      'ingest-conflicts': 'Ingest conflicts',
      'csv-import': 'CSV import',
      bills: 'Bills',
      invoices: 'Invoices',
      rates: 'Rates',
      wallets: 'Wallet accounts',
      journals: 'Journals',
      payouts: 'Payout runs',
      'ledger-check': 'Ledger check',
      'sports-requirements': 'Requirements',
      'sports-registrations': 'Registrations',
      'sports-quota-tokens': 'Quota tokens',
      'report-pack': 'Management pack',
      'report-transactions': 'Transaction search',
      'report-vessels': 'Vessel activity',
      'report-audit': 'Audit trail',
      partners: 'Partners',
      'api-keys': 'API keys',
      'request-log': 'Request log',
      webhooks: 'Webhooks',
      'api-docs': 'API documentation',
      'erp-mapping': 'ERP & exports',
      'md-collection-points': 'Collection points',
      'md-vessels': 'Vessels',
      'md-fishermen': 'Fishermen',
      'md-fish-types': 'Fish types',
      'md-scales': 'Scales',
      announcements: 'Announcements',
      'profile-changes': 'Profile changes',
      'my-sessions': 'My sessions',
      'my-security': 'Two-factor',
    },
  },

  /** Full-page states shown outside a normal screen: 403, 404, auth frame. */
  errors: {
    forbiddenTitle: 'Not available to you',
    forbiddenSubtitle: 'Your role does not include this screen',
    forbiddenMeansTitle: 'What this means',
    forbiddenAddressPrefix: 'The address you asked for was',
    forbiddenBody:
      'Hiding a screen is a convenience — the server enforces the same rules on every request, so nothing was exposed. If you need this screen, an administrator can grant the permission to your role.',
    notFoundTitle: 'Page not found',
    notFoundSubtitle: 'There is nothing at this address',
  },


  shell: {
    sectionsLabel: 'Sections',
    skipToContent: 'Skip to content',
    openNav: 'Open navigation',
    closeNav: 'Close navigation',
    primaryNav: 'Primary navigation',
    catalogErrorTitle: 'Reference data could not be loaded',
    renderErrorTitle: 'This screen stopped responding',
    renderErrorBody: 'The page hit an error while rendering. Nothing was sent to the server.',
    renderErrorRetry: 'Try rendering again',
    accountLoading: 'Loading your account',
    locale: {
      toDhivehi: 'Switch to Dhivehi (ދިވެހި)',
      toEnglish: 'Switch to English',
    },
    theme: {
      toDark: 'Switch to dark theme',
      toSystem: 'Follow the system theme',
      toLight: 'Switch to light theme',
      change: 'Change theme',
    },
    site: {
      label: 'Collection point',
      all: 'All sites',
      allHint: 'Every collection point',
      mine: 'My sites',
    },
    user: {
      menuLabel: 'Account',
      mySessions: 'My sessions',
      twoFactorOn: 'Two-factor (on)',
      twoFactorOff: 'Two-factor (off)',
      signOut: 'Sign out',
      signOutTitle: 'Sign out?',
      signOutMessage: 'Anything you have typed and not saved will be lost.',
      signedOut: 'Signed out',
    },
  },

  /** Keyed by the role id the API sends. Ids are persisted and never renamed. */
  roles: {
    superadmin: 'Superadmin',
    md_executive: 'Managing Director',
    hq_ops: 'HQ operations',
    site_supervisor: 'Site supervisor',
    collector_operator: 'Collector operator',
    finance: 'Finance',
    auditor: 'Auditor',
    partner: 'Partner',
  },

  dashboard: {
    exec: {
      title: 'Executive dashboard',
      period: 'Period',
      range: {
        last30: 'Last 30 days',
        last90: 'Last 90 days',
        thisMonth: 'This month to date',
        lastMonth: 'Last full month',
      },
      comparedWith: 'Compared with {{range}}',
      noComparison: 'No comparison — nothing recorded in {{range}}',
      errorTitle: 'The dashboard could not be loaded',
      tilesLabel: 'Headline figures',
      collectedToday: 'Collected today',
      collectedTodayHelp:
        'Net weight landed today across every collection point in scope, from rpt.site_daily.',
      vsYesterday: 'vs yesterday',
      vsPrevious: 'vs previous period',
      purchaseValue: 'Purchase value',
      purchaseValueHelp:
        'Total value of fish purchased in the selected period, before bills are finalized.',
      payoutsPaid: 'Payouts paid',
      payoutsPaidHelp: 'Value of payout runs completed in the period, from rpt.payouts_daily.',
      activeVessels: 'Active vessels',
      landedToday: 'Landed today',
      activeVesselsHelp:
        'Distinct vessels with at least one session today. No comparison is shown because the reporting views do not carry a prior-day vessel count.',
      sportsShare: 'Sports share',
      sportsShareHelp: 'Share of collected weight arriving through the sports-fishing channel.',
      devicesOffline: 'Devices offline',
      devicesOfflineHelp: 'Scales that have not sent a heartbeat inside the offline threshold.',
      trendsLabel: 'Trends',
      bySiteTitle: 'Collection by site',
      bySiteSubtitle: 'Net weight landed per day',
      bySiteBlended: 'Blended — more than {{count}} collection points',
      bySiteChartLabel: 'Net weight landed per day, one line per collection point',
      bySiteEmpty: 'No landings in this period',
      seriesCollected: 'Collected',
      valueTitle: 'Purchase value',
      valueSubtitle: 'This period against the one before it',
      valueSubtitleAlone: 'Purchase value per day',
      valueChartLabel: 'Daily purchase value for this period and the previous period',
      valueEmpty: 'No purchases in this period',
      seriesThisPeriod: 'This period',
      seriesPreviousPeriod: 'Previous period',
      compositionLabel: 'Composition',
      fishMixTitle: 'Fish mix',
      fishMixSubtitle: 'Share of weight by species',
      fishMixChartLabel: 'Share of collected weight by fish type',
      fishMixCentre: '{{code}} leads the mix',
      fishMixEmpty: 'No landings to break down',
      sitesTitle: 'Sites by weight',
      sitesSubtitle: 'Total for the period',
      sitesChartLabel: 'Net weight landed by collection point',
      sitesEmpty: 'No landings to rank',
      comparisonTitle: 'Period comparison',
      comparisonSubtitle: '{{current}} vs {{previous}}',
      comparisonCaption:
        'Headline metrics for the selected period compared with the preceding period of the same length',
      colMetric: 'Metric',
      colThisPeriod: 'This period',
      colPrevious: 'Previous',
      colChange: 'Change',
      rowCollected: 'Collected',
      rowPurchaseValue: 'Purchase value',
      rowBillsIssued: 'Bills issued',
      rowPayoutsPaid: 'Payouts paid',
      rowSportsShare: 'Sports share',
      rowDevicesOffline: 'Devices offline',
      comparisonEmpty: 'Nothing to compare yet.',
      thisPeriodCaption: 'Headline metrics for the selected period',
      noPreviousData:
        'Nothing was collected, billed or paid out between {{range}}, so this period has nothing to be compared with. The figures below are for the selected period alone.',
      provenance:
        'Every figure here is read from the rpt reporting views, refreshed by the worker.',
      periodTotals: 'Period totals:',
    },

    ops: {
      title: 'Operations',
      subtitle: "Today's landings so far, and what is running right now",
      live: 'Live',
      notLive: 'Not receiving updates',
      refreshedAgo: 'refreshed {{when}}',
      never: 'never',
      errorTitle: 'The operations view could not be loaded',
      todayLabel: 'Today so far',
      tilesLabel: 'Right now',
      landedToday: 'Landed today',
      landedTodayHelp:
        'Net weight weighed in since midnight Maldives time, across the collection points in scope. A collector vessel closes its tally offline and syncs afterwards, so finalized sessions count here too — not only the ones still open.',
      valueToday: 'Value landed today',
      valueTodayHelp:
        "What today's weight is worth at the rates frozen onto each entry as it was captured. The bills behind it may still be drafts.",
      sessionsToday: 'Sessions today',
      sinceMidnight: 'Since 00:00 Maldives time',
      reportingSites: '{{count}} collection points reporting',
      throughput: 'Throughput',
      lastMinutes: 'Last {{minutes}} minutes',
      throughputHelp:
        'Net weight in the trailing window, scaled to an hour. It is a rate, so it falls back to zero when nothing has been weighed lately — the day so far is in the tiles above.',
      kgPerHour: '{{value}} kg/h',
      openSessions: 'Open sessions',
      openSessionsHelp:
        'Sessions still being weighed. A device tally arrives already closed, so most of a working day never passes through this tile.',
      openNoneNow: 'Nothing being weighed',
      openWeight: '{{weight}} on the scales',
      openStragglers: '{{count}} open since before today',
      waitingInQueue: 'Waiting in queue',
      devicesOffline: 'Devices offline',
      depthTitle: 'Queue depth by site',
      depthSubtitle: 'Waiting and being served right now',
      depthWaiting: 'waiting',
      depthServing: '{{count}} serving',
      depthEmptyTitle: 'No queue activity',
      depthEmptyDescription: 'No collection point has anyone waiting.',
      sessionsTitle: "Today's sessions, and anything still open",
      sessionsTableLabel: "Sessions from today's business day, plus sessions still open from any day",
      colSite: 'Site',
      colVessel: 'Vessel',
      colStatus: 'Status',
      colStarted: 'Started',
      colEntries: 'Entries',
      colNetSoFar: 'Net so far',
      stragglerBadge: 'since {{day}}',
      sessionsEmptyTitle: 'Nothing has landed yet today',
      sessionsEmptyDescription:
        'Sessions appear here the moment a collector vessel syncs or a supervisor opens one. Earlier days are under Collection → Sessions.',
      healthTitle: 'Device health',
      healthTableLabel: 'Scale device heartbeat health',
      colDevice: 'Device',
      colLink: 'Link',
      colLastHeartbeat: 'Last heartbeat',
      colApp: 'App',
      colPending: 'Pending',
      colConnectivity: 'Connectivity',
      healthEmptyTitle: 'No devices enrolled',
      healthEmptyDescription: 'Issue an enrollment code from the device registry.',
      note: 'Today so far starts again at midnight Maldives time. Throughput is a {{window}} window. Average wait times per site are on the queue board.',
    },
  },

  billing: {
    /** Shared between BillListView and BillDetailView. */
    bills: {
      title: 'Bills',
      type: 'Type',
      channel: 'Channel',
      settledFrom: 'Settled from',

      list: {
        subtitle: 'Fish, ice and diesel — drafts, finalized documents and voids',
        searchPlaceholder: 'Bill number on this page',
        chipBillNo: 'Bill no on this page',
        errorTitle: 'Bills could not be loaded',
        colBillNo: 'Bill no',
        anyType: 'Any type',
        anyStatus: 'Any status',
        statusDraft: 'Draft',
        statusFinalized: 'Finalized',
        statusVoided: 'Voided',
        anyChannel: 'Any channel',
        channelRegular: 'Regular',
        channelSports: 'Sports',
        noNumberBadge: 'draft — no number yet',
        reversedBadge: 'reversed',
        pageTotal: 'This page',
        emptyTitle: 'No bills match these filters',
        emptyDescription: 'Widen the date range, or clear the status filter.',
        note: 'A draft has no document number: numbers are minted inside the finalize transaction so the per-site series stays gapless.',
      },

      detail: {
        title: 'Bill',
        draftTitle: 'Draft bill {{id}}',
        sessionButton: 'Session',
        pdfButton: 'PDF',
        voidButton: 'Void',
        finalizeButton: 'Finalize',
        errorTitle: 'This bill could not be loaded',
        voidedTitle: 'This bill has been voided',
        reversedByPrefix: 'Reversed by',
        voidedBody: 'The document itself is unchanged and keeps its place in the series.',
        draftAlertTitle: 'Draft — not yet a document',
        draftAlertBody:
          'A draft has no number and has posted nothing to the ledger. Finalizing mints the number inside the transaction, which is what keeps the per-site series gapless.',
        totalsTitle: 'Totals',
        subtotal: 'Subtotal',
        discount: 'Discount',
        linesSumTo: 'Lines sum to',
        quantity: 'Quantity',
        finalizedAt: 'Finalized',
        verifyCode: 'Verify code',
        journal: 'Journal',
        voidReason: 'Void reason',
        reasonLabel: 'Reason',
        reasonHint: 'Required. It appears on the reversal document and in the audit trail.',
        linesTitle: 'Lines',
        linesTableLabel: 'Bill lines',
        colSeq: '#',
        colDescription: 'Description',
        colPcs: 'Pcs',
        lockedBadge: 'locked',
        linesEmptyTitle: 'No lines on this bill',
        linesEmptyDescription:
          "A supply bill carries a single line; a fish bill is built from the session's entries.",
        voidCardTitle: 'Void this bill',
        voidCardSubtitle: 'A reversal document will be issued',
        voidSubmit: 'Void bill',
        finalizeConfirmTitle: 'Finalize this bill?',
        finalizeConfirmMessage:
          'Finalizing mints the document number, posts the double-entry journal, and makes the bill immutable. A correction afterwards is a void that issues a reversal document — the bill itself can never be edited.',
        finalizeConfirmLabel: 'Finalize bill',
        finalizeToastTitle: 'Bill finalized',
        finalizeErrorTitle: 'Could not finalize',
        voidConfirmTitle: 'Void this bill?',
        voidConfirmMessage:
          'A reversal document is issued against it and the wallet movement is reversed. The original keeps its number and stays in the series — nothing is deleted.',
        voidToastTitle: 'Bill voided',
        voidToastBody: 'A reversal document was issued.',
        voidErrorTitle: 'Could not void the bill',
        pdfErrorTitle: 'The PDF could not be prepared',
      },
    },

    /** Shared between InvoiceListView and InvoiceDetailView. */
    invoices: {
      title: 'Invoices',
      paid: 'Paid',
      outstanding: 'Outstanding',

      list: {
        subtitle: 'What is owed, what has been paid, and what remains',
        verifyButton: 'Check cached totals',
        driftAlertTitleSingular:
          '{{count}} invoice total disagrees with the payments recorded against it',
        driftAlertTitlePlural:
          '{{count}} invoice totals disagree with the payments recorded against them',
        driftLine: 'cached {{cached}}, derived {{derived}}',
        driftBody:
          'This is a cache disagreeing with the ledger, not money going missing. Finance should look at it before the next payout run.',
        okTitle: 'Every cached total matches',
        okToast: 'Every invoice total matches its payments',
        verifyErrorTitle: 'The check did not complete',
        errorTitle: 'Invoices could not be loaded',
        colInvoiceNo: 'Invoice no',
        anyStatus: 'Any status',
        statusOpen: 'Open',
        statusPartPaid: 'Part paid',
        statusPaid: 'Paid',
        statusCancelled: 'Cancelled',
        emptyTitle: 'No invoices match these filters',
        outstandingOnPage: 'Outstanding on this page',
      },

      detail: {
        title: 'Invoice',
        due: 'Due',
        settled: 'Settled',
        recordPaymentButton: 'Record payment',
        errorTitle: 'This invoice could not be loaded',
        balanceTitle: 'Balance',
        invoiced: 'Invoiced',
        paymentsSumTo: 'Payments sum to',
        recordPaymentTitle: 'Record a payment',
        paymentErrorTitle: 'The payment was refused',
        method: 'Method',
        methodCash: 'Cash',
        methodBankTransfer: 'Bank transfer',
        methodWalletOffset: 'Wallet offset',
        referenceHint: 'Bank reference or receipt number.',
        receivedOn: 'Received on',
        paymentsTitle: 'Payments',
        paymentsEmptyTitle: 'No payments recorded',
        paymentsEmptyDescription: 'Nothing has been received against this invoice yet.',
        paymentsTableLabel: 'Payments against this invoice',
        colReceived: 'Received',
        invoiceLabel: 'Invoice',
        recordConfirmTitle: 'Record this payment?',
        recordConfirmMessage:
          'A payment posts a balanced journal against the invoice. Recording one that did not happen has to be corrected with a reversal, not deleted.',
        recordToast: 'Payment recorded',
        recordErrorTitle: 'The payment was not recorded',
      },
    },

    /** RateAdminView.vue — one screen: the published-rate table, the resolver, and the publish modal. */
    rates: {
      title: 'Rates',
      subtitle: 'Effective-dated, frozen onto every entry at capture',
      tableLabel: 'Published rates',
      kind: 'Kind',
      grade: 'Grade',
      colWithIce: 'With ice',
      colState: 'State',
      colNote: 'Note',
      anyKind: 'Any kind',
      anyGrade: 'Any grade',
      anySite: 'Any site',
      anyLabel: 'any',
      allLabel: 'all',
      openLabel: 'open',
      withIceBadge: 'with ice',
      stateActive: 'active',
      stateExpired: 'expired',
      statePending: 'pending',
      alertTitle: 'Publishing never rewrites history',
      alertBody:
        'A rate is resolved when an entry is captured and frozen onto it. Publishing a new rate — including one dated in the future — changes what happens next and nothing that has already happened. That is why there is no edit action on this screen.',
      resolverTitle: 'What rate applies?',
      resolverSubtitle: 'Ask the server, rather than reading it off the table',
      resolverOn: 'On',
      withIceSwitch: 'With ice',
      resolveButton: 'Resolve',
      resolveErrorTitle: 'No rate resolved',
      matchedBy: 'Matched by',
      perUnit: 'per {{unit}}',
      effectiveFrom: 'Effective from',
      effectiveTo: 'Effective to',
      specificity0: 'default — no site, no fish type',
      specificity1: 'fish type',
      specificity2: 'site and fish type',
      specificity3: 'site, fish type and grade',
      activeOn: 'Active on',
      errorTitle: 'Rates could not be loaded',
      emptyTitle: 'No rates match these filters',
      closeAction: 'Close',
      publishTitle: 'Publish a rate',
      publishErrorTitle: 'The rate was not published',
      fishTypeHint: 'Leave empty for a rate that applies to any species.',
      rateHint: 'Per kilogram for fish and ice, per litre for diesel.',
      withIceSwitchLabel: 'Applies to catches landed with ice',
      effectiveFromHint: 'May be in the future. Entries captured before this keep their own rate.',
      effectiveToHint: 'Leave empty for open-ended.',
      publishSubmit: 'Publish rate',
      publishToastTitle: 'Rate published',
      publishToastBody: 'Entries captured from its start date will use it.',
      closeConfirmTitle: 'Close this rate?',
      closeConfirmMessage:
        'The rate stops applying from the date you choose. Entries already captured under it keep the rate frozen onto them — closing never rewrites history.',
      closeConfirmLabel: 'Close rate',
      closingOn: 'Closing on',
      closeToastTitle: 'Rate closed',
      closeErrorTitle: 'Could not close the rate',
    },
  },

  finance: {
    /** JournalListView.vue — the ledger browser: filters, list, and the entries modal. */
    journals: {
      title: 'Journals',
      subtitle: 'Every movement, as a balanced double-entry posting',
      tableLabel: 'Ledger journals',
      anySource: 'Any source',
      sourceFishBill: 'Fish bill',
      sourceIceBill: 'Ice bill',
      sourceDieselBill: 'Diesel bill',
      sourcePayout: 'Payout',
      sourceAdjustment: 'Adjustment',
      sourceReversal: 'Reversal',
      errorTitle: 'Journals could not be loaded',
      colJournalNo: 'Journal no',
      colOccurred: 'Occurred',
      colReverses: 'Reverses',
      emptyTitle: 'No journals match these filters',
      modalTitleFallback: 'Journal',
      modalErrorTitle: 'The journal could not be loaded',
      factPosted: 'Posted',
      entriesTableLabel: 'Journal entries',
      entriesEmptyTitle: 'No legs on this journal',
      entriesEmptyDescription:
        'A journal with no entries should not exist — quote the journal number to support.',
      balancedBadge: 'balanced',
      notBalancedBadge: 'NOT balanced',
    },

    /** LedgerCheckView.vue — `fcms verify-ledger`, run from the back office. */
    ledgerCheck: {
      title: 'Ledger check',
      subtitle: 'Recompute every balance from the journals and report any drift',
      runAgain: 'Run again',
      runCheck: 'Run the check',
      lastRun: 'Last run {{time}}',
      infoTitle: 'This reads the whole ledger',
      infoBody:
        "Every account is recomputed from its journals and every journal's legs are summed. It can take a while on a busy month; nothing is written.",
      errorTitle: 'The ledger check did not complete',
      runningLabel: 'Recomputing every balance',
      notCheckedTitle: 'The ledger has not been checked in this session',
      notCheckedDescription: 'Run the check to recompute every balance from the journals.',
      okTitle: 'The books balance',
      okBody:
        "Every cached balance matches its journals, every journal's debits equal its credits, and the grand total across all accounts is zero.",
      badTitle: 'The ledger check found problems',
      badBody:
        'Do not run a payout until these are understood. Each section below names exactly what disagrees.',
      tilesLabel: 'Ledger check results',
      statAccounts: 'Accounts checked',
      statJournals: 'Journals checked',
      statEntries: 'Entries checked',
      statGrandTotal: 'Grand total',
      grandTotalHelp: 'The sum of every account balance. In a double-entry ledger this is zero, always.',
      statOrphans: 'Orphan entries',
      orphansHelp: 'Ledger entries with no journal behind them. There should never be any.',
      driftCardTitle: 'Cached balances that disagree with the ledger',
      driftTableLabel: 'Accounts whose cached balance differs from the recomputed one',
      colCached: 'Cached',
      colDerived: 'Derived',
      colDifference: 'Difference',
      unbalancedCardTitle: 'Journals that do not balance',
      unbalancedTableLabel: 'Journals whose debits do not equal their credits',
      colJournal: 'Journal',
      colLegs: 'Legs',
      otherFindingsTitle: 'Other findings',
    },

    /** WalletAccountsView.vue and WalletStatementView.vue. */
    wallets: {
      balance: 'Balance',
      balanceErrorTitle: 'The balance could not be read',

      list: {
        title: 'Wallet accounts',
        subtitle: 'Balances are derived from the ledger; the stored figure is a cache',
        journalsButton: 'Journals',
        ledgerCheckButton: 'Ledger check',
        readBalancesButton: 'Read balances on this page',
        searchPlaceholder: 'Account label',
        anyKind: 'Any kind',
        kindFishermanWallet: 'Fisherman wallet',
        kindBankClearing: 'Bank clearing',
        kindFishPayable: 'Fish payable',
        kindIceRevenue: 'Ice revenue',
        kindDieselRevenue: 'Diesel revenue',
        kindPartnerPayable: 'Partner payable',
        kindCash: 'Cash',
        kindReceivable: 'Receivable',
        chipLabelContains: 'Label contains',
        errorTitle: 'Accounts could not be loaded',
        colOwner: 'Owner',
        colLedgerCheck: 'Ledger check',
        emptyTitle: 'No accounts match',
        syncMatches: 'matches ledger',
        syncDiffers: 'cache differs',
        notRead: 'not read',
        readingBalance: 'Reading balance',
        actionStatement: 'Statement',
        note:
          'A "cache differs" row is not money going missing — it is the stored balance disagreeing with the sum of the journals. The Ledger check screen recomputes every account at once and is the right place to look at it.',
      },

      statement: {
        title: 'Account statement',
        breadcrumbStatement: 'Statement',
        labelStored: 'Stored balance',
        labelDerived: 'Recomputed from the ledger',
        labelAgreement: 'Agreement',
        agreementMatches: 'matches',
        agreementDiffers: 'cache differs from ledger',
        labelAsOf: 'As of',
        driftAlertTitle: "This account's cached balance disagrees with its journals",
        driftAlertBody:
          'The ledger is the record; the cached figure is a convenience. Run the ledger check before this account is included in a payout run.',
        errorTitle: 'The statement could not be loaded',
        tableLabel: 'Account statement lines',
        colDate: 'Date',
        colMovement: 'Movement',
        emptyTitle: 'No movements in this period',
        emptyDescription: 'Widen the date range, or clear it entirely.',
      },
    },

    /** PayoutRunListView.vue and PayoutRunDetailView.vue. */
    payouts: {
      list: {
        subtitle: 'Draft, approve, generate the bank file, confirm, reconcile',
        newRun: 'New run',
        anyStatus: 'Any status',
        statusDraft: 'Draft',
        statusApproved: 'Approved',
        statusFileGenerated: 'File generated',
        statusConfirmed: 'Confirmed',
        statusReconciled: 'Reconciled',
        errorTitle: 'Payout runs could not be loaded',
        colRunNo: 'Run no',
        colPeriodFrom: 'Period from',
        colPeriodTo: 'Period to',
        colRail: 'Rail',
        emptyTitle: 'No payout runs',
        emptyDescription: 'Create one for a period once its bills are finalized.',
        modalTitle: 'New payout run',
        modalNote:
          'A run gathers every fisherman with a positive wallet balance from finalized bills in the period. It is created as a DRAFT and pays nobody until it is approved and the bank file is generated.',
        sitePlaceholder: 'Choose a collection point',
        createErrorTitle: 'The run was not created',
        createSubmit: 'Create draft run',
        toastCreatedTitle: 'Draft run created',
        toastCreatedBody: 'Review the items before approving.',
      },

      detail: {
        titleFallback: 'Payout run',
        breadcrumbRun: 'Run',
        errorTitle: 'This payout run could not be loaded',
        stepsAriaLabel: 'Run progress',
        stepDraftLabel: 'Draft',
        stepDraftHint: 'Items selected. Nobody has been paid.',
        stepApprovedLabel: 'Approved',
        stepApprovedHint: 'Signed off. Still no money has moved.',
        stepFileGeneratedLabel: 'File generated',
        stepFileGeneratedHint: 'Bank file produced, journals posted.',
        stepConfirmedLabel: 'Confirmed',
        stepConfirmedHint: "The bank's result has been applied.",
        stepReconciledLabel: 'Reconciled',
        stepReconciledHint: 'Matched against the ledger and closed.',
        approveButton: 'Approve',
        generateButton: 'Generate bank file',
        downloadButton: 'Download file',
        reconcileButton: 'Reconcile',
        tilesLabel: 'Run totals',
        statPayees: 'Payees',
        statPaid: 'Paid',
        statFailed: 'Failed',
        statUnmatched: 'Unmatched',
        unmatchedHelp:
          'Lines the bank returned that do not correspond to anything we sent. They are never applied.',
        driftAlertTitle: 'The item amounts do not sum to the run total',
        driftAlertBody:
          'The run reports {{runTotal}} laari; the items on screen sum to {{itemsTotal}} laari. Both figures are shown unaltered.',
        runCardTitle: 'Run',
        detailRail: 'Payment rail',
        detailSha: 'File SHA-256',
        detailLabelRun: 'Run',
        detailLabelPayees: 'Payees',
        detailLabelFailedBecause: 'Failed because',
        detailNotStated: 'not stated',
        confirmApproveTitle: 'Approve this payout run?',
        confirmApproveMessage:
          'Approval signs off on who is being paid and how much. No money moves yet — the bank file is generated as a separate step.',
        confirmApproveLabel: 'Approve run',
        toastApproved: 'Run approved',
        toastApproveErrorTitle: 'The run was not approved',
        confirmGenerateTitle: 'Generate the bank transfer file?',
        confirmGenerateMessage:
          'This posts the payout journals — wallets are debited and the bank clearing account is credited — and produces the file for the bank. It is the point at which the money is committed.',
        confirmGenerateLabel: 'Generate file',
        toastGeneratedTitle: 'File generated',
        toastGeneratedBody: '{{bytes}} bytes · SHA-256 {{sha}}…',
        toastGenerateErrorTitle: 'The file was not generated',
        toastFileLinkErrorTitle: 'The file link could not be prepared',
        confirmApplyTitle: "Apply the bank's confirmation?",
        confirmApplyMessage:
          'Item statuses, the wallet journals for the paid lines, and the run status are all updated in one transaction. A row whose amount or account differs from what we sent is reported as an exception and NOT applied.',
        confirmApplyLabel: 'Apply confirmation',
        detailLabelFile: 'File',
        detailFileFallback: 'uploaded file',
        toastAppliedTitle: 'Confirmation applied',
        toastAppliedBody: '{{paid}} paid, {{failed}} failed, {{unmatched}} unmatched',
        toastApplyErrorTitle: 'The confirmation was not applied',
        confirmReconcileTitle: 'Reconcile this run?',
        confirmReconcileMessage:
          'Reconciling matches the run against the ledger and closes it. Do this once the bank statement agrees with what is recorded here.',
        confirmReconcileLabel: 'Reconcile run',
        toastReconciled: 'Run reconciled',
        toastReconcileErrorTitle: 'The run was not reconciled',
        confirmRetryTitle: 'Retry this payment?',
        confirmRetryMessage: 'The item is put back into the run for the next file. Nothing is paid twice.',
        confirmRetryLabel: 'Retry item',
        detailLabelPayee: 'Payee',
        toastRetried: 'Item queued for retry',
        toastRetryErrorTitle: 'The item was not retried',
        uploadCardTitle: "Upload the bank's confirmation",
        uploadCardSubtitle: 'The raw file the bank returned',
        uploadNote:
          'The file is sent exactly as it is, byte for byte — repackaging it would be a chance to mangle it. A row whose amount or account differs from what we sent comes back as an exception and is not applied: a bank that moved a different amount is a conversation, not a status update.',
        chooseFileButton: 'Choose the confirmation file',
        applyConfirmationButton: 'Apply confirmation',
        confirmationCardTitle: 'What the confirmation did',
        confirmationSubtitle: '{{paid}} paid · {{failed}} failed · {{unmatched}} unmatched',
        totalAppliedLabel: 'Total applied',
        exceptionsLabel: 'Exceptions — not applied',
        itemsCardTitle: 'Items',
        itemsEmptyTitle: 'No items in this run',
        itemsEmptyDescription: 'Nobody in this period has a positive wallet balance from finalized bills.',
        itemsTableLabel: 'Payout items',
        colPayee: 'Payee',
        colBank: 'Bank',
        colBankReference: 'Bank reference',
        colFailure: 'Failure',
        reasonBadge: 'reason',
        retryButton: 'Retry',
        separation:
          'Generating the file needs {{runPerm}}; confirming it needs {{confirmPerm}}. The two are separate so no single person can both select who is paid and declare that the bank paid them.',
      },
    },
  },

  collection: {
    sessions: {
      list: {
        title: 'Collection sessions',
        subtitle: 'Every vessel visit, from draft through billed',
        manualEntry: 'Manual entry',
        statusAny: 'Any status',
        statusDraft: 'Draft',
        statusOpen: 'Open',
        statusFinalized: 'Finalized',
        statusBilled: 'Billed',
        searchPlaceholder: 'Filter this page by vessel',
        siteShort: 'Site',
        chipVesselContains: 'Vessel contains',
        errorTitle: 'Sessions could not be loaded',
        tableLabel: 'Collection sessions',
        colSource: 'Source',
        colStarted: 'Started',
        colFlags: 'Flags',
        flagChainBreak: 'chain break',
        flagTotalsDiffer: 'totals differ',
        emptyTitle: 'No sessions match these filters',
        emptyDescription: 'Clear a filter, or switch to another collection point.',
      },
      detail: {
        loadErrorTitle: 'This session could not be loaded',
        breadcrumbList: 'Collection sessions',
        titleWithRef: 'Session {{ref}}',
        openBill: 'Open bill',
        finalize: 'Finalize',
        chainBreakTitle: 'Integrity chain break',
        chainBreakBody:
          "This session's {{field}} does not follow the previous session from the same device. That means a session was dropped, reordered, or replayed. The chain is witnessed rather than verified — the device's HMAC key never leaves the device — so this is evidence to investigate, not proof of tampering.",
        totalsDiscrepancyTitle: 'Device totals differ from the entries',
        totalsDiscrepancyBody:
          'The totals the device reported are not the sum of the entries it sent. Both are shown below, unaltered.',
        manualSessionTitle: 'Manually captured session',
        manualSessionBody:
          'This session was entered by hand rather than read from a scale. It is reported as a manual-entry exception.',
        totalsCardTitle: 'Totals',
        totalsCardSubtitle: 'As reported, and as summed from the entries',
        reportedNetWeight: 'Reported net weight',
        summedFromEntries: 'Summed from entries',
        reportedValue: 'Reported value',
        pieces: 'Pieces',
        voidedCount: '({{count}} voided)',
        integrityCardTitle: 'Integrity',
        integrityCardSubtitle: 'Edge-to-platform evidence, witnessed',
        entriesEmptyTitle: 'No entries on this session',
        entriesEmptyDescription: 'Nothing has been weighed against this vessel visit yet.',
        entriesTableLabel: 'Weighing entries for this session',
        colGrade: 'Grade',
        colTare: 'Tare',
        colCapture: 'Capture',
        detailOperator: 'Operator',
        detailStarted: 'Started',
        detailFinalized: 'Finalized',
        detailSource: 'Entry source',
        detailIce: 'With ice',
        detailTemp: 'Fish temperature',
        detailTempValue: '{{value}} °C',
        detailPortalRef: 'Portal reference',
        detailReportedSite: 'Site reported by device',
        integrityChainHead: 'ScalApp chain head',
        integrityPrevHash: 'Previous session hash',
        integrityLocalSeq: 'Device sequence',
        qualityManual: 'manual',
        qualityUnstable: 'unstable',
        qualityVoided: 'voided',
        qualityLocked: 'locked',
        voidAction: 'Void',
        voidReasonFallback: 'voided',
        finalizeConfirmTitle: 'Finalize this session?',
        finalizeConfirmMessage:
          'Finalizing locks every entry and creates a draft fish bill. Entries cannot be edited or voided afterwards — a correction becomes a reversal document.',
        finalizeConfirmLabel: 'Finalize session',
        finalizeSuccessTitle: 'Session finalized',
        finalizeSuccessBody: 'A draft fish bill is waiting in Billing.',
        finalizeErrorTitle: 'Could not finalize',
        voidCardTitle: 'Void entry',
        voidCardSubtitle: 'Entry #{{seq}}',
        voidNote:
          'Voiding keeps the entry in the tally with its reason and your name against it. It is excluded from billing and retained for audit.',
        voidReasonLabel: 'Reason',
        voidReasonHint: 'Required. This is written to the audit trail.',
        voidSubmit: 'Void entry',
        voidSuccessTitle: 'Entry voided',
        voidSuccessBody: 'It stays in the tally, marked and attributed.',
        voidErrorTitle: 'Could not void the entry',
      },
    },

    manualEntry: {
      title: 'Manual entry',
      subtitle: 'Weighings captured by hand, outside a scale device',
      startAnother: 'Start another',
      openSession: 'Open session',
      warningTitle: 'Every entry on this screen is flagged',
      warningBody:
        'Manual entries carry {{code}} through to the bill and the reports, are listed as exceptions in the management pack, and are written to the audit trail against your account. Use a scale device wherever one is working.',
      loadErrorTitle: 'That session could not be loaded',
      openSessionCardTitle: 'Open a session',
      openSessionCardSubtitle: 'Choose the vessel this tally belongs to',
      createErrorTitle: 'Could not open the session',
      sitePlaceholder: 'Choose a collection point',
      vesselEmptyText: 'No vessel matches',
      operatorRefLabel: 'Operator reference',
      operatorRefHint: 'Who is doing the weighing. Recorded on the session.',
      withIceLabel: 'Landed with ice',
      sessionOpenToastTitle: 'Session open',
      sessionOpenToastBody: 'Every entry you add is flagged as manual.',
      entryCardTitle: 'Add an entry — {{vessel}}',
      addErrorTitle: 'The entry was not added',
      gradeDefaultOption: 'Default grade',
      piecesLabel: 'Pieces',
      grossWeightLabel: 'Gross weight',
      tareWeightLabel: 'Tare weight',
      tareWeightHint: 'Crate or basket. Leave empty for none.',
      remarksLabel: 'Remarks',
      addEntryButton: 'Add entry',
      entriesCardTitle: 'Entries so far',
      entriesTableLabel: 'Entries captured in this manual session',
      colSeq: '#',
      entriesEmptyTitle: 'Nothing captured yet',
      entriesEmptyDescription: 'Add the first weighing above.',
      finalizeButton: 'Finalize session',
      finalizeConfirmTitle: 'Finalize this manual session?',
      finalizeConfirmMessage:
        'Finalizing locks every entry and creates a draft fish bill. Because these entries were captured by hand, the session is reported as a manual-entry exception.',
      finalizeConfirmLabel: 'Finalize session',
      finalizeSuccessTitle: 'Session finalized',
      finalizeErrorTitle: 'Could not finalize',
    },

    queue: {
      title: 'Queue board',
      sitePlaceholder: 'Choose a collection point',
      closeQueue: 'Close queue',
      live: 'Live',
      notLive: 'Not receiving updates',
      chooseSiteTitle: 'Choose a collection point',
      chooseSiteDescription: 'The queue is per site per day.',
      errorTitle: 'The queue board could not be loaded',
      streamDownTitle: 'The live feed is not connected',
      streamDownBody:
        'The board below is a snapshot and will not update on its own. Press Refresh before calling a vessel.',
      statsAriaLabel: 'Queue statistics',
      statWaiting: 'Waiting',
      statServing: 'Being served',
      statAverageWait: 'Average wait',
      statP90Wait: '90th percentile wait',
      statServedToday: '{{count}} served today',
      p90Help:
        'Nine in ten vessels waited less than this. The average hides the bad afternoon; this does not.',
      issueCardTitle: 'Issue a token',
      issueCardSubtitle: 'For a vessel arriving at the counter',
      issueVesselHint: 'Optional — a token can be issued without one.',
      issueButton: 'Issue token',
      loadingLabel: 'Loading the queue',
      servingColumn: 'Serving ({{count}})',
      calledColumn: 'Called ({{count}})',
      waitingColumn: 'Waiting ({{count}})',
      closedColumn: 'Closed today ({{count}})',
      sinceTime: 'since {{time}}',
      calledTime: 'called {{time}}',
      doneButton: 'Done',
      startButton: 'Start',
      noShowButton: 'No-show',
      callButton: 'Call',
      nobodyServing: 'Nobody being served',
      nobodyCalled: 'Nobody called',
      queueEmpty: 'Queue is empty',
      nothingClosed: 'Nothing closed yet',
      noShowConfirmTitle: 'Mark token {{number}} as a no-show?',
      noShowConfirmMessage:
        'The vessel loses its place in the queue and has to take a new token. This cannot be undone.',
      noShowConfirmLabel: 'Mark no-show',
      tokenErrorTitle: 'Token {{number}} did not move',
      issueSuccessTitle: 'Token {{number}} issued',
      issueErrorTitle: 'Could not issue a token',
      closeConfirmTitle: 'Close today’s queue?',
      closeConfirmMessage:
        'Every token still waiting, called or being served is marked no-show and the queue day is closed. Use this at the end of the day.',
      closeConfirmLabel: 'Close the queue',
      closeDetailStillOpen: 'Still open',
      closeSuccessTitle: 'Queue closed for today',
      closeErrorTitle: 'Could not close the queue',
    },
  },

  devices: {
    registry: {
      title: 'Device registry',
      subtitle: 'Every ScalApp device, and the collection point its token is bound to',
      fleetHealth: 'Fleet health',
      issueEnrolmentCode: 'Issue enrolment code',
      errorTitle: 'The device registry could not be loaded',
      emptyTitle: 'No devices at this collection point',
      emptyDescription: 'Issue an enrolment code, then enter it on the device.',
      actionOpen: 'Open',
      revokeConfirmMessage:
        'The device token stops working immediately. The device keeps whatever it has captured offline and will not be able to sync it until it is enrolled again.',
      revokeErrorTitle: 'Could not revoke the device',
      enrolModalTitle: 'Issue an enrolment code',
      enrolErrorTitle: 'The code was not issued',
      codeLabel: 'Enrolment code',
      codeInstruction:
        'Enter this code on the device to bind it. It expires, and it can only be used once.',
      issuedExpires: 'Expires',
      formSiteHint: 'The device token will be bound to this site and can never write for another.',
      formNameHint: 'What is written on the tablet — “Kooddoo jetty 1”.',
      done: 'Done',
      issueCode: 'Issue code',
    },

    detail: {
      titleFallback: 'Device',
      breadcrumbList: 'Device registry',
      rename: 'Rename',
      loadErrorTitle: 'This device could not be loaded',
      renameCardTitle: 'Rename device',
      detailAppVersion: 'App version',
      detailScaleVersion: 'Scale version',
      detailEnrolled: 'Enrolled',
      notEnrolled: 'not enrolled',
      detailRevoked: 'Revoked',
      aliasCardTitle: 'Reference aliases',
      aliasCardSubtitle: "What this device's local catalog ids mean",
      aliasNote:
        'A session captured before a catalog sync carries the device’s own ids. Ingest never guesses at one — it fails with {{code}} naming the field, and the mapping is made here, deliberately.',
      aliasErrorTitle: 'The alias was not saved',
      aliasKindLabel: 'Kind',
      aliasRefLabel: 'Device reference',
      aliasRefHint: 'Exactly as the device sends it — vs-1, ft-3.',
      aliasTargetLabel: 'Resolves to',
      saveAlias: 'Save alias',
      aliasColKind: 'Kind',
      aliasColRef: 'Device reference',
      aliasColTarget: 'Resolves to',
      aliasSavedTitle: 'Alias saved',
      aliasSavedBody: 'Sessions carrying that reference will now resolve.',
      aliasEmptyTitle: 'No aliases',
      aliasEmptyDescription:
        'This device sends server ids, or has not sent an unresolved reference yet.',
      revokeConfirmMessage:
        'Its token stops working immediately and it cannot sync until it is enrolled again.',
      revokeErrorTitle: 'Could not revoke',
      renameSuccessTitle: 'Device renamed',
      renameErrorTitle: 'Could not rename',
    },

    health: {
      title: 'Fleet health',
      subtitle: 'Heartbeats, pending syncs and link quality',
      deviceRegistry: 'Device registry',
      metaText: 'Refreshes every minute · last read {{when}}',
      errorTitle: 'Fleet health could not be loaded',
      statDevices: 'Devices',
      statOffline: 'Offline',
      statPending: 'Entries waiting to sync',
      statPendingHelp:
        'Weighings captured on a device that have not reached the server. They are safe on the device; they are not yet billable.',
      statLowBattery: 'Low battery',
      tilesAriaLabel: 'Fleet summary',
      loadingLabel: 'Loading fleet health',
      emptyTitle: 'No devices are reporting',
      emptyDescription: 'Enrol a device from the registry, or widen the collection point filter.',
      online: 'online',
      offline: 'offline',
      lastHeartbeat: 'last heartbeat {{when}}',
      batteryLabel: 'Battery',
      factPending: 'Pending',
      factLink: 'Link',
    },

    csvImport: {
      title: 'CSV import',
      subtitle: "Check a ScalApp tallies.csv against the server's own parser",
      clear: 'Clear',
      infoTitle: 'This screen checks the file; it does not submit it',
      infoBody:
        'CSV ingest is a collector-surface operation ({{endpoint}}) authenticated by a device token, so a staff session cannot send one. Everything below is computed here, with the same header and the same money and weight conversions the server uses, so a file that passes this check is a file the server will accept.',
      chooseFileCardTitle: 'Choose a file',
      dropText: 'Drop {{filename}} here, or',
      chooseFile: 'Choose a file',
      formatSummary: 'Expected columns, in order',
      formatNote:
        'Money in this file is a decimal MVR string — the opposite of the JSON path, which carries integer laari. The conversion here is exact integer arithmetic on the digits; a third decimal is truncated rather than rounded.',
      refusedTitle: 'The file was refused',
      unreadableFile: 'The file could not be read as text. Is it a CSV?',
      tilesAriaLabel: 'What is in the file',
      statSessions: 'Sessions',
      statEntryRows: 'Entry rows',
      statManual: 'Manual weighings',
      statFindings: 'Sessions with findings',
      problemsTitle: 'Rows that could not be read',
      sessionsCardTitle: 'Sessions in this file',
      tableLabel: 'Sessions parsed from the tallies file',
      colBillNo: 'Bill no',
      colReportedSite: 'Reported site',
      colFinalized: 'Finalized',
      colFindings: 'Findings',
      emptyTitle: 'No sessions in this file',
      manualBadge: '{{count}} manual',
      findingBadgeOne: '{{count}} finding',
      findingBadgeMany: '{{count}} findings',
      findingsCardTitle: 'Findings',
      findingsCardSubtitle: 'What the server would refuse',
      findingHead: '{{ref}} · {{vessel}}',
      noFileTitle: 'No file loaded',
      noFileDescription: 'Choose a tallies.csv exported from a ScalApp device.',
    },

    conflicts: {
      title: 'Ingest conflicts',
      subtitle: 'Sessions that arrived twice with different contents',
      scopeLabel: 'Scope',
      scopeOpen: 'Open only',
      openBannerTitleOne: '{{count}} conflict waiting for review',
      openBannerTitleMany: '{{count}} conflicts waiting for review',
      openBannerBody:
        'The stored session is whatever arrived first. Nothing here has changed a financial record; the second payload was quarantined and an audit alarm was raised.',
      errorTitle: 'Conflicts could not be loaded',
      emptyTitle: 'No conflicts',
      emptyDescription:
        'Every session that arrived twice arrived identically, which is what a replay should look like.',
      colDetected: 'Detected',
      colStoredHash: 'Stored hash',
      colRejectedHash: 'Rejected hash',
      colState: 'State',
      actionInspect: 'Inspect',
      actionReviewed: 'Reviewed',
      modalTitle: 'Quarantined payload',
      factStoredHash: 'Stored payload hash',
      factRejectedHash: 'Rejected payload hash',
      payloadNote:
        'Below is the payload that was REJECTED. It is shown so a supervisor can see what the device tried to send; the stored session is unchanged. Site names resolve through the catalog where the ids are known.',
      markReviewed: 'Mark reviewed',
      resolveConfirmTitle: 'Mark this conflict reviewed?',
      resolveConfirmMessage:
        'This records that you have examined the quarantined payload. It does NOT change the stored session — the first payload received remains the record, and the quarantined one stays for audit.',
      resolveConfirmLabel: 'Mark reviewed',
      resolveDetailDetected: 'Detected',
      resolveSuccessTitle: 'Conflict marked reviewed',
      resolveErrorTitle: 'Could not update the conflict',
    },
  },

  masterdata: {
    includeArchived: 'Include archived',

    /**
     * The singular and plural of every entity `ResourceScreen.vue` renders,
     * keyed by the camelCase form of its `noun` prop ("fish type" →
     * `fishType`). The screen looks the pair up rather than composing English
     * text, because "New {{noun}}" and "Search {{plural}}" have to be
     * translatable SENTENCES — Dhivehi puts the verb last, so gluing a
     * translated verb onto a translated noun in English order produces
     * nonsense.
     *
     * The plural is stored, never derived: `noun + 's'` gives "fishermans",
     * and there is no rule at all for Thaana.
     */
    nouns: {
      vessel: { singular: 'vessel', plural: 'vessels' },
      fishType: { singular: 'fish type', plural: 'fish types' },
      grade: { singular: 'grade', plural: 'grades' },
      fisherman: { singular: 'fisherman', plural: 'fishermen' },
      scale: { singular: 'scale', plural: 'scales' },
      collectionPoint: { singular: 'collection point', plural: 'collection points' },
    },

    /**
     * ResourceScreen.vue — the shell all six master-data screens are made of.
     * Every string here takes the entity's noun as a parameter, so one wording
     * serves all six and a seventh entity needs only a `nouns` entry.
     */
    resource: {
      newAction: 'New {{noun}}',
      searchPlaceholder: 'Search {{plural}}',
      chipIncluding: 'Including',
      chipArchived: 'archived',
      listErrorTitle: 'The {{noun}} list could not be loaded',
      emptyTitle: 'No {{plural}} match',
      emptyDescriptionWritable: 'Create one, or clear the filters.',
      emptyDescriptionReadOnly: 'Clear the filters to see more.',
      colState: 'State',
      badgeActive: 'active',
      badgeArchived: 'archived',
      archiveAction: 'Archive',
      restoreAction: 'Restore',
      newTitle: 'New {{noun}}',
      editTitle: 'Edit {{noun}}',
      createSubmit: 'Create {{noun}}',
      saveErrorTitle: 'The {{noun}} was not saved',
      createdToast: '{{Noun}} created',
      updatedToast: '{{Noun}} updated',
      archivedToast: '{{Noun}} archived',
      restoredToast: '{{Noun}} restored',
      archiveConfirmTitle: 'Archive this {{noun}}?',
      archiveConfirmMessage:
        'It disappears from pickers and new records, and every document that already references it is untouched. Nothing is deleted — archiving is reversible.',
      archiveConfirmAction: 'Archive {{noun}}',
      archiveErrorTitle: 'Could not archive the {{noun}}',
      restoreErrorTitle: 'Could not restore the {{noun}}',
    },
    vessels: {
      list: {
        title: 'Vessels',
        subtitle: 'Dhoani and collector vessels on the register',
        colRegistration: 'Registration',
        registrationHint: 'How the vessel is identified on a bill and by a scale device.',
        colCapacity: 'Capacity',
        colLength: 'Length (m)',
        lengthLabel: 'Length in metres',
        colLegacyRef: 'Legacy ref',
        colOwner: 'Owner',
        ownerLabel: 'Owner',
        ownerHint: 'The fisherman a fish bill is paid to. A vessel with no owner cannot be billed.',
        noOwner: 'No owner',
        crewAction: 'Crew',
      },
    },

    fishTypes: {
      list: {
        title: 'Fish types',
        subtitle: 'The species catalog every entry and rate refers to',
        codeHint: 'Short and stable — it appears in the catalog a scale device syncs.',
        scientificName: 'Scientific name',
        gradesAction: 'Grades',
      },
    },

    fishGrades: {
      list: {
        pageTitle: 'Grades — {{fishType}}',
        screenTitle: 'Grades of {{fishType}}',
        subtitle: 'Only meaningful inside their species',
        breadcrumbFishTypes: 'Fish types',
        breadcrumbGrades: 'Grades',
        colOrder: 'Order',
        colDefault: 'Default',
        defaultBadge: 'default',
        sortOrderLabel: 'Sort order',
        sortOrderHint: 'Where this grade appears in a picker. Lower comes first.',
        defaultLabel: 'Default grade',
        defaultHint:
          'Used when a device sends an entry with no grade — ScalApp has no grade concept at all.',
      },
    },

    fishermen: {
      list: {
        title: 'Fishermen',
        subtitle: 'The register behind every wallet, bill and payout',
        colNationalId: 'National id',
        nationalIdHint:
          'Encrypted at rest. It is the identity the sports registration verifies against.',
        colPhone: 'Phone',
        phoneHint: 'Used for the portal one-time code and for announcements.',
        addressLabel: 'Address',
        colBank: 'Bank',
        colAccount: 'Account',
        bankAccountLabel: 'Bank account',
        bankAccountHint: 'Where payout runs send money. Encrypted at rest and masked in the payout file.',
        colSports: 'Sports',
        sportsLabel: 'Sports fisherman',
        sportsBadge: 'sports',
        statusSuspended: 'Suspended',
      },
    },

    scales: {
      list: {
        title: 'Scales',
        subtitle: 'Weighing instruments, per collection point',
        colSerial: 'Serial',
        serialLabel: 'Serial number',
        colModel: 'Model',
        modelPlaceholder: 'WPL R10',
      },
    },

    collectionPoints: {
      list: {
        title: 'Collection points',
        subtitle: 'Where fish is landed, weighed and billed',
        codeHint:
          'Short and unique. It becomes part of every document number minted here, so it cannot be changed later.',
        colType: 'Type',
        typeCollectorVessel: 'Collector vessel',
        typeJetty: 'Jetty',
        typeCollectionCentre: 'Collection centre',
        typeProcessingFacility: 'Processing facility',
        colTimezone: 'Time zone',
        timezoneHint: 'IANA name. Indian/Maldives unless there is a reason.',
        colCreated: 'Created',
      },
    },

    crew: {
      pageTitle: 'Crew — {{vessel}}',
      subtitle: 'Who is aboard, and who was',
      breadcrumbVessels: 'Vessels',
      breadcrumbCrew: 'Crew',
      errorTitle: 'The crew list could not be loaded',
      addCardTitle: 'Add a crew member',
      addErrorTitle: 'The crew member was not added',
      role: 'Role',
      rolePlaceholder: 'Captain',
      addButton: 'Add',
      crewCardTitle: 'Crew',
      emptyTitle: 'No crew recorded',
      emptyDescription: 'Add the people who fish from this vessel.',
      tableLabel: 'Crew members of this vessel',
      currentBadge: 'current',
      endAction: 'End',
      addedToast: 'Crew member added',
      endConfirmTitle: 'End this crew membership?',
      endConfirmMessage:
        'The record stays with an end date on it. Nothing that references it changes — a bill landed while they were aboard is still theirs.',
      endConfirmLabel: 'End membership',
      endDetailEnding: 'Ending',
      endedToast: 'Membership ended',
      endErrorTitle: 'Could not end the membership',
    },
  },

  settings: {
    security: {
      title: 'Two-factor authentication',
      subtitle: 'A code from an authenticator app, in addition to your password',
      on: 'on',
      off: 'off',
      genericErrorTitle: 'That did not work',
      onCardTitle: 'Two-factor is on',
      onCardBody:
        'You are asked for a six-digit code from your authenticator app after your password at every sign-in.',
      turnOff: 'Turn off two-factor',
      offCardTitle: 'Turn on two-factor',
      whyTitle: 'Why this is worth two minutes',
      whyBody:
        "Back-office accounts sign in on shared machines at collection points and can finalize bills, publish rates and approve payouts. A password alone is the only thing between someone else's afternoon and those actions.",
      beginEnrolment: 'Begin enrolment',
      finishCardTitle: 'Finish enrolment',
      finishCardSubtitle: 'Add the secret to your app, then confirm a code',
      secretLabel: 'Secret',
      secretInstruction:
        'Type this into your authenticator app now. It is not shown again — beginning enrolment a second time mints a different secret.',
      uriIntro: 'Or use this URI, if your app accepts one',
      notFinishedTitle: 'Enrolment is not finished until a code is verified',
      notFinishedBody:
        'A secret that was generated and never confirmed would lock you out at the next sign-in. Enter a code from the app below.',
      codeLabel: 'Code from your app',
      confirmAndTurnOn: 'Confirm and turn on',
      turnedOnToast: 'Two-factor authentication is on',
      turnedOnToastBody: 'You will be asked for a code at every sign-in.',
      turnOffConfirmTitle: 'Turn off two-factor authentication?',
      turnOffConfirmMessage:
        'Your account will be protected by its password alone. On a shared machine at a collection point that is a meaningful reduction in protection.',
      turnOffConfirmLabel: 'Turn it off',
      turnedOffToast: 'Two-factor authentication is off',
      turnOffErrorTitle: 'It was not turned off',
    },

    mySessions: {
      title: 'My sessions',
      subtitle: 'Every place this account is signed in',
      signOutEverywhere: 'Sign out everywhere',
      errorTitle: 'Your sessions could not be loaded',
      emptyTitle: 'No sessions',
      emptyDescription: 'That should not be possible while you are reading this — reload the page.',
      tableLabel: 'Sessions for this account',
      address: 'Address',
      colSignedIn: 'Signed in',
      colExpires: 'Expires',
      unknownDevice: 'unknown device',
      stateRevoked: 'revoked',
      stateActive: 'active',
      revokeConfirmTitle: 'Sign out this session?',
      revokeConfirmMessage: 'Whoever is using it is signed out immediately and has to authenticate again.',
      revokeConfirmLabel: 'Revoke session',
      revokedToast: 'Session revoked',
      revokeErrorTitle: 'The session was not revoked',
      revokeAllConfirmTitle: 'Sign out everywhere?',
      revokeAllConfirmMessage:
        'Every session is revoked, including the one you are using right now. You will be returned to the sign-in screen.',
      revokeAllConfirmLabel: 'Sign out everywhere',
      activeSessionsLabel: 'Active sessions',
      revokedAllToastOne: '{{count}} session revoked',
      revokedAllToastMany: '{{count}} sessions revoked',
      revokeAllErrorTitle: 'Sessions were not revoked',
      note:
        'Only the SHA-256 of a session token is stored server-side, so a database leak yields no usable sessions. Revocation takes effect immediately.',
    },
  },

  integrations: {
    partnerLabel: 'Partner',
    everyPartner: 'Every partner',
    choosePartner: 'Choose a partner',
    doneCopiedSecret: 'Done — I have copied the secret',

    apiKeys: {
      list: {
        title: 'API keys',
        subtitle: 'Partner credentials for /partner/v1 — HMAC-signed, scoped, revocable',
        requestLogAction: 'Request log',
        issueKeyAction: 'Issue key',
        errorTitle: 'API keys could not be loaded',
        tableLabel: 'Partner API keys',
        colKey: 'Key',
        colKeyId: 'Key id',
        colScopes: 'Scopes',
        colRateLimit: 'Rate limit',
        colLastUsed: 'Last used',
        colExpires: 'Expires',
        emptyTitle: 'No API keys',
        emptyDescription: 'Issue one for a registered partner.',
        noneBadge: 'none',
        usageAction: 'Usage',
        stateActive: 'active',
        stateExpired: 'expired',
        stateRevoked: 'revoked',
        createModalTitle: 'Issue an API key',
        createErrorTitle: 'The key was not issued',
        secretLabel: 'Secret',
        secretInstruction:
          'This secret signs every request from this partner and is shown exactly once. The server keeps only an argon2 hash of it, so it cannot be shown again — a lost secret means issuing a new key.',
        issuedKeyIdLabel: 'Key id — sent as X-Api-Key',
        issuedNameLabel: 'Name',
        issuedScopesLabel: 'Scopes',
        scopeWarningTitle: 'The scope list could not be read from the partner API document',
        scopeWarningBody:
          'Enter scopes by hand below, separated by spaces or commas. They are validated server-side either way.',
        partnerNoOptionsHint: 'No partners are registered. Register one first.',
        keyNameLabel: 'Key name',
        keyNameHint: 'What this key is for — "Fuel supplier production".',
        scopesLabel: 'Scopes',
        scopeUsedBy: 'Used by {{operations}}',
        scopesTextHint: 'Space or comma separated, for example: catalog.read fuel.issue',
        expiresOnLabel: 'Expires on',
        expiresOnHint: 'Leave empty for a key with no expiry.',
        rateLimitLabel: 'Rate limit per minute',
        ipAllowlistLabel: 'IP allowlist',
        ipAllowlistHint: 'Optional. Space or comma separated. Empty means any address.',
        revokeConfirmTitle: 'Revoke this key?',
        revokeConfirmMessage:
          'The key stops working in this process immediately. Another API replica may honour it for up to ten seconds, because key lookups are cached for that long — a documented and deliberate limit, not a bug.',
        revokeConfirmLabel: 'Revoke key',
        revokeDetailKey: 'Key',
        revokeDetailKeyId: 'Key id',
        revokedToast: 'Key revoked',
        revokeErrorTitle: 'The key was not revoked',
      },

      detail: {
        titleFallback: 'API key',
        breadcrumb: 'API keys',
        breadcrumbCurrent: 'Key',
        revokedBadge: 'revoked',
        errorTitle: 'This key could not be loaded',
        tilesLabel: 'Usage totals',
        statRequests: 'Requests',
        statSuccessful: 'Successful',
        statClientErrors: 'Client errors',
        statClientErrorsHelp:
          "A climbing 4xx line is usually a signature, clock-skew or scope problem at the partner's end.",
        statServerErrors: 'Server errors',
        chartCardTitle: 'Requests per day',
        chartCardSubtitle: 'Split by status class',
        chartLabel: 'Daily request counts for this key, split by response status class',
        chartEmptyTitle: 'This key has not been used',
        chartEmptyDescription: 'Nothing has been signed with it yet.',
        seriesSuccess: 'Success (2xx)',
        seriesClientError: 'Client error (4xx)',
        seriesServerError: 'Server error (5xx)',
        keyCardTitle: 'Key',
        detailKeyId: 'Key id (X-Api-Key)',
        detailScopes: 'Scopes',
        detailRate: 'Rate limit',
        detailRateValue: '{{value}} per minute',
        detailAllowlist: 'IP allowlist',
        detailAnyAddress: 'any address',
        detailExpires: 'Expires',
        detailLastUsed: 'Last used',
        detailRevoked: 'Revoked',
        recentRequestsCardTitle: 'Recent requests',
        tableLabel: 'Recent partner requests made with this key',
        colWhen: 'When',
        colMethod: 'Method',
        colPath: 'Path',
        colLatency: 'Latency',
        colSource: 'Source',
        colRequestId: 'Request id',
        latencyValue: '{{value}} ms',
        emptyTitle: 'No requests logged',
        emptyDescription: 'Nothing has been signed with this key yet.',
      },
    },

    webhooks: {
      list: {
        title: 'Webhooks',
        subtitle: 'Where platform events are delivered, and what happened to them',
        registerEndpoint: 'Register endpoint',
        errorTitle: 'Webhook endpoints could not be loaded',
        tableLabel: 'Webhook endpoints',
        colEndpoint: 'Endpoint',
        colEvents: 'Events',
        emptyTitle: 'No webhook endpoints',
        emptyDescription: 'Register one so a partner is told when a bill is finalized.',
        stateActive: 'active',
        stateInactive: 'inactive',
        deliveriesAction: 'Deliveries',
        createModalTitle: 'Register a webhook endpoint',
        createErrorTitle: 'The endpoint was not registered',
        secretLabel: 'Signing secret',
        secretInstruction:
          'This secret signs every delivery to this endpoint so the partner can verify it came from us. It is shown once. Rotating it invalidates the old one immediately.',
        issuedEndpointLabel: 'Endpoint:',
        urlLabel: 'Endpoint URL',
        urlHint: 'Must be http or https and must not resolve to a private, loopback or metadata address.',
        eventsLabel: 'Events',
        createdToast: 'Webhook registered',
      },

      detail: {
        title: 'Webhook endpoint',
        breadcrumb: 'Webhooks',
        breadcrumbCurrent: 'Endpoint',
        inactiveBadge: 'inactive',
        rotateAction: 'Rotate secret',
        deactivateAction: 'Deactivate',
        errorTitle: 'This endpoint could not be loaded',
        newSecretCardTitle: 'New signing secret',
        secretLabel: 'Signing secret',
        secretInstruction:
          'The previous secret stopped working the moment this was minted. Give this to the partner now.',
        cardTitle: 'Endpoint',
        detailEndpoint: 'Endpoint',
        detailDescription: 'Description',
        detailEvents: 'Subscribed events',
        detailActive: 'Active',
        detailLastChange: 'Last change',
        inactiveAlertTitle: 'Deliveries are stopped',
        inactiveAlertBody:
          'This endpoint is inactive. Events are not being delivered to it; the log below is history.',
        rotateConfirmTitle: 'Rotate the signing secret?',
        rotateConfirmMessage:
          'A new secret is minted and the old one stops working immediately. Every delivery signed with the old secret will fail verification at the partner until they update it, so agree the change with them first.',
        rotateConfirmLabel: 'Rotate secret',
        rotateErrorTitle: 'The secret was not rotated',
        deactivateConfirmTitle: 'Deactivate this endpoint?',
        deactivateConfirmMessage:
          'Deliveries stop. The endpoint is never physically deleted — the delivery log references it forever — so this is reversible by editing it back to active.',
        deactivateConfirmLabel: 'Deactivate',
        deactivatedToast: 'Endpoint deactivated',
        deactivateErrorTitle: 'The endpoint was not deactivated',
        deliveryErrorTitle: 'The delivery log could not be loaded',
        deliveryCardTitle: 'Delivery log',
        deliveryCardSubtitle: 'One row per attempt',
        tableLabel: 'Webhook delivery attempts',
        colAttempted: 'Attempted',
        colEvent: 'Event',
        colAttempt: 'Attempt',
        colResponse: 'Response',
        colDuration: 'Duration',
        colNextRetry: 'Next retry',
        colError: 'Error',
        failedBadge: 'failed',
        latencyValue: '{{value}} ms',
        replayAction: 'Replay',
        emptyTitle: 'No deliveries yet',
        emptyDescription: 'Nothing subscribed to has happened since this endpoint was registered.',
        editModalTitle: 'Edit endpoint',
        editUrlLabel: 'Endpoint URL',
        editDescriptionLabel: 'Description',
        eventsLabel: 'Events',
        saveErrorTitle: 'The endpoint was not saved',
        savedToast: 'Endpoint updated',
        replayConfirmTitle: 'Replay this delivery?',
        replayConfirmMessage:
          "The original event is sent again as a fresh attempt, and the attempt number continues from this delivery's history so the log reads as one sequence.",
        replayConfirmLabel: 'Replay delivery',
        replayDetailEvent: 'Event',
        replayDetailAttempt: 'Attempt',
        deliveredToastTitle: 'Delivered',
        deliveredToastBody: 'HTTP {{status}} in {{duration}} ms',
        replayFailedTitle: 'The replay was attempted and failed',
        replayErrorTitle: 'The delivery could not be replayed',
      },
    },

    partners: {
      list: {
        title: 'Partners',
        subtitle: 'Organizations that hold API keys and receive webhooks',
        apiKeysAction: 'API keys',
        registerAction: 'Register partner',
        errorTitle: 'Partners could not be loaded',
        tableLabel: 'Partner organizations',
        colContact: 'Contact',
        colEmail: 'Email',
        colPhone: 'Phone',
        emptyTitle: 'No partners registered',
        emptyDescription: 'Register one before issuing an API key.',
        stateActive: 'active',
        stateArchived: 'archived',
        keysAction: 'Keys',
        archiveAction: 'Archive',
        restoreAction: 'Restore',
        createModalTitle: 'Register a partner',
        editModalTitle: 'Edit partner',
        saveErrorTitle: 'The partner was not saved',
        nameLabel: 'Name',
        kindPlaceholder: 'fuel_supplier',
        kindHint: 'A short machine-ish label. It is how partner profiles are grouped.',
        contactNameLabel: 'Contact name',
        contactEmailLabel: 'Contact email',
        contactPhoneLabel: 'Contact phone',
        registerSubmit: 'Register partner',
        updatedToast: 'Partner updated',
        registeredToast: 'Partner registered',
        archiveConfirmTitle: 'Archive this partner?',
        archiveConfirmMessage:
          'They disappear from the pickers on NEW keys and NEW webhooks. Existing keys keep working — revoke those separately if that is what you mean.',
        archiveConfirmLabel: 'Archive partner',
        archivedToast: 'Partner archived',
        archiveErrorTitle: 'Could not archive the partner',
        restoredToast: 'Partner restored',
        restoreErrorTitle: 'Could not restore the partner',
      },
    },

    requestLog: {
      title: 'Partner request log',
      subtitle: 'Every signed call, with its outcome and latency',
      errorTitle: 'The request log could not be loaded',
      everyKey: 'Every key',
      keyLabel: 'Key',
      pathLabel: 'Path',
      pathPlaceholder: '/partner/v1/fuel',
      sinceLabel: 'Since',
      untilLabel: 'Until',
      rowsLabel: 'Rows',
      ceilingAlertTitle: 'Showing the newest {{limit}} rows',
      ceilingAlertBody:
        'There may be more. Narrow the date range or raise the row count — this endpoint returns a bounded slice rather than a paginated set.',
      tableLabel: 'Partner API request log',
      colWhen: 'When',
      colKey: 'Key',
      colMethod: 'Method',
      colPath: 'Path',
      colLatency: 'Latency',
      colSource: 'Source',
      colRequestId: 'Request id',
      latencyValue: '{{value}} ms',
      emptyTitle: 'No requests match',
      emptyDescription: 'Widen the date range, or clear the key filter.',
      chipPathStartsWith: 'Path starts with',
      chipSince: 'Since',
      chipUntil: 'Until',
    },

    erpExport: {
      title: 'ERP & exports',
      subtitle: 'Journal batches for the accounting system, and the government catch return',
      unmappedAlertTitle: 'Some account kinds have no GL mapping',
      unmappedLead:
        'A journal line whose account has no GL code exports with a blank column and the accountant has to guess. Unmapped:',
      mapCardTitle: 'GL account map',
      mapCardSubtitle: 'Wallet account kind to accounting code',
      mapErrorTitle: 'The account map could not be loaded',
      mapEmptyTitle: 'No accounts mapped',
      mapEmptyDescription: 'Map each wallet account kind to its GL code before the first export.',
      tableLabel: 'ERP GL account mapping',
      colAccountKind: 'Wallet account kind',
      colGlCode: 'GL code',
      colGlName: 'GL account name',
      colLastChange: 'Last change',
      accountKindLabel: 'Account kind',
      glCodeLabel: 'GL code',
      glNameLabel: 'GL account name',
      saveMapping: 'Save mapping',
      mappingSavedToast: 'Mapping saved',
      mapSaveErrorTitle: 'The mapping was not saved',
      journalCardTitle: 'ERP journal export',
      journalCardSubtitle: 'One row per posted journal line',
      downloadCsv: 'Download CSV',
      journalNote:
        'Covers finalized documents in the window. A deployment with no journal reader wired returns {{code}}, which this screen reports rather than downloading an empty file.',
      journalDownloadedToast: 'Journal export downloaded',
      journalErrorTitle: 'The ERP export did not complete',
      govCardTitle: 'Government catch statistics',
      govCardSubtitle: 'One row per fish type, per month',
      periodLabel: 'Period',
      periodHint: 'Year and month, as 2026-08.',
      govDownloadedToast: 'Catch statistics downloaded',
      govErrorTitle: 'The catch-statistics export did not complete',
      historyErrorTitle: 'The export history could not be loaded',
      historyCardTitle: 'Export history',
      historyCardSubtitle: 'What has been generated, and its checksum',
      historyTableLabel: 'Previous ERP and government exports',
      colGenerated: 'Generated',
      colAdapter: 'Adapter',
      colPeriod: 'Period',
      colRows: 'Rows',
      colSha: 'SHA-256',
      historyEmptyTitle: 'No exports generated yet',
    },

    apiDocs: {
      fallbackTitle: 'Partner API',
      versionLabel: 'Version {{version}}',
      downloadAction: 'Download OpenAPI',
      reloadAction: 'Reload',
      errorTitle: 'The API document could not be loaded',
      loadingLabel: 'Loading the API document',
      specTitle: 'This page is the specification',
      specBody:
        'Everything below is rendered from {{path}} as the server exports it. If a partner operation is not here, it does not exist; if it is here, the middleware enforces the scope shown against it.',
      tabsLabel: 'API documentation sections',
      tabEndpoints: 'Endpoints',
      tabAuth: 'Authentication',
      tabSchemas: 'Schemas',
      filterLabel: 'Filter',
      filterPlaceholder: 'Filter by path, summary, operation or scope',
      noEndpointTitle: 'No endpoint matches',
      noEndpointDescription: 'Clear the filter to see the whole surface.',
      colName: 'Name',
      colIn: 'In',
      colType: 'Type',
      colField: 'Field',
      parametersHeading: 'Parameters',
      requestBodyHeading: 'Request body',
      responseHeading: 'Response',
      requiredBadge: 'required',
      yesValue: 'yes',
      noValue: 'no',
      authIntroTitle: 'How a partner authenticates',
      authIntro1: 'Every {{path}} request carries three headers: {{h1}} (the key id, not the secret), {{h2}} (unix seconds), and {{h3}}.',
      authFormula: 'The signature is {{formula}}, where the canonical string is {{canonical}}.',
      authListSkew:
        "A request more than five minutes from server time is rejected with {{code}}. Synchronise the caller's clock; do not widen the window at your end, because you cannot.",
      authListReplay:
        'A repeated signature is rejected with {{code}}. The nonce cache is what makes a captured request useless.',
      authListIdempotency:
        'Every partner write requires an {{header}}. Retrying with the same key replays the original response byte-identically; the same key with a different body is refused with {{code}} rather than silently treated as a replay.',
      authListScope:
        'A key carries scopes. A call outside them is refused with {{code}} — the operation exists, the credential does not reach it.',
      walkthroughTitle: 'Signing walkthrough',
      walkthroughSubtitle: 'Copy, paste, substitute your key',
      copiedToast: 'Example copied',
      copyFailedToastTitle: 'Could not copy automatically',
      copyFailedToastBody: 'Select the example and copy it by hand.',
      securitySchemesTitle: 'Security schemes',
      noSchemasTitle: 'This document defines no named schemas',
      noSchemasDescription: 'Its operations describe their payloads inline.',
    },
  },

  sports: {
    quotaTokens: {
      title: 'Quota tokens',
      subtitle: 'Entitlements to land against a published requirement',
      whoseTitle: 'Whose tokens?',
      chooseFishermanTitle: 'Choose a fisherman',
      chooseFishermanDescription: 'Tokens are held by a person, so the list is per fisherman.',
      errorTitle: 'Tokens could not be loaded',
      tableLabel: 'Quota tokens held by this fisherman',
      colRequirement: 'Requirement',
      colAllocated: 'Allocated',
      colRedeemed: 'Redeemed',
      colChannel: 'Channel',
      emptyTitle: 'No tokens',
      emptyDescription: 'This fisherman holds no quota tokens. Issue one from a published requirement.',
      billAction: 'Bill',
      cancelConfirmTitle: 'Cancel this quota token?',
      cancelConfirmMessage:
        'The allocated weight returns to the requirement and the fisherman can no longer land against this token.',
      cancelConfirmLabel: 'Cancel token',
      cancelDetailAllocated: 'Allocated',
      cancelledToast: 'Token cancelled',
      cancelledToastBody: 'The weight is back on the requirement.',
      cancelErrorTitle: 'The token was not cancelled',
      note:
        'Redeeming a token happens at the collection point and produces a session and a sports-tagged bill in one transaction. This screen issues and cancels; it does not redeem.',
    },

    requirements: {
      list: {
        title: 'Purchase requirements',
        subtitle: 'What HQ is buying from sports and recreational fishermen',
        newAction: 'New requirement',
        errorTitle: 'Requirements could not be loaded',
        anyStatus: 'Any status',
        statusDraft: 'Draft',
        statusPublished: 'Published',
        statusClosed: 'Closed',
        tableLabel: 'Sports purchase requirements',
        colTarget: 'Target',
        colRemaining: 'Remaining',
        colFilled: 'Filled',
        colWindow: 'Window',
        filledLabel: 'Filled',
        emptyTitle: 'No requirements match these filters',
        emptyDescription: 'Publish one to open a site to sports landings.',
        createModalTitle: 'New purchase requirement',
        createErrorTitle: 'The requirement was not created',
        createNote:
          'Created as a DRAFT. It becomes visible on the public pages, and quota tokens can be issued against it, only once it is published.',
        chooseSite: 'Choose a collection point',
        targetWeightLabel: 'Target weight',
        ratePerKgLabel: 'Rate per kg',
        windowOpensLabel: 'Window opens',
        windowClosesLabel: 'Window closes',
        createSubmit: 'Create draft',
        createdToast: 'Requirement created as a draft',
        createdToastBody: 'Publish it to make it public.',
      },

      detail: {
        titleFallback: 'Requirement',
        breadcrumb: 'Purchase requirements',
        breadcrumbCurrent: 'Requirement',
        publishAction: 'Publish',
        issueTokenAction: 'Issue quota token',
        closeAction: 'Close',
        errorTitle: 'This requirement could not be loaded',
        draftAlertTitle: 'Not published yet',
        draftAlertBody: 'A draft is invisible on the public pages and no quota token can be issued against it.',
        tilesLabel: 'Requirement figures',
        statTarget: 'Target',
        statRemaining: 'Remaining',
        statRemainingHelp: 'Maintained by the server against issued quota tokens. Never computed in the browser.',
        statRatePerKg: 'Rate per kg',
        statFilled: 'Filled',
        cardTitle: 'Requirement',
        detailWindowOpens: 'Window opens',
        detailWindowCloses: 'Window closes',
        detailPublished: 'Published',
        publishConfirmTitle: 'Publish this requirement?',
        publishConfirmMessage:
          'It becomes visible on the public pages and quota tokens can be issued against it. Publishing is how a sports fisherman learns they can land here.',
        publishConfirmLabel: 'Publish',
        publishDetailSite: 'Site',
        publishDetailTarget: 'Target',
        publishedToast: 'Requirement published',
        publishErrorTitle: 'Could not publish',
        closeConfirmTitle: 'Close this requirement?',
        closeConfirmMessage:
          'No further quota tokens can be issued against it. Tokens already issued keep their allocation and can still be redeemed.',
        closeConfirmLabel: 'Close requirement',
        closeDetailRemaining: 'Remaining',
        closedToast: 'Requirement closed',
        closeErrorTitle: 'Could not close the requirement',
        issueModalTitle: 'Issue a quota token',
        issueModalSubtitle: 'Gated by the remaining weight',
        issueErrorTitle: 'The token was not issued',
        fishermanHint: 'The sports registration must be active.',
        allocatedWeightLabel: 'Allocated weight',
        allocatedWeightHint: 'Cannot exceed the remaining weight — the server checks, and refuses with {{code}}.',
        validForHoursLabel: 'Valid for (hours)',
        issueSubmit: 'Issue token',
        issuedToast: 'Quota token issued for {{weight}} kg',
        note:
          'Redeeming a token creates a session and a bill tagged {{channel}}, which then flows through the ordinary pipeline — the same finalize, the same ledger, the same payout run.',
      },
    },

    registrations: {
      title: 'Sports registrations',
      subtitle: "Look up one fisherman's registration",
      findCardTitle: 'Find a registration',
      findNote:
        'Registrations are looked up per fisherman rather than browsed. Each one carries a verified national id and phone number, and there is no list endpoint for them by design.',
      notFoundErrorTitle: 'No registration found',
      cardTitle: 'Registration',
      detailNidVerified: 'National id verified',
      detailVerifiedOn: 'Verified on',
      detailPhoneVerified: 'Phone verified',
      notVerified: 'not verified',
      detailLastChange: 'Last change',
      suspendAction: 'Suspend registration',
      suspendConfirmTitle: 'Suspend this sports registration?',
      suspendConfirmMessage:
        'No further quota tokens can be issued to this fisherman. Tokens already issued are unaffected — cancel those individually if that is what you mean.',
      suspendConfirmLabel: 'Suspend registration',
      suspendedToast: 'Registration suspended',
      suspendErrorTitle: 'Could not suspend the registration',
      chooseFishermanTitle: 'Choose a fisherman',
      chooseFishermanDescription: 'Start typing a name or a national id.',
      notFoundTitle: 'No registration for this fisherman',
      notFoundDescription: 'They have not completed the sports registration flow.',
    },
  },

  auth: {
    login: {
      title: 'Sign in',
      errorTitle: 'Could not sign in',
      usernameLabel: 'Username',
      passwordLabel: 'Password',
      submit: 'Sign in',
    },

    totp: {
      title: 'Two-factor code',
      subtitle: 'Enter the six-digit code from your authenticator app.',
      errorTitle: 'That code was not accepted',
      codeLabel: 'Authentication code',
      verify: 'Verify',
      startOver: 'Start over',
    },
  },

  reports: {
    audit: {
      list: {
        title: 'Audit trail',
        subtitle: 'Append-only, hash-chained, PII masked',
        actorLabel: 'Actor',
        actorAny: 'Any actor',
        actorStaff: 'Staff',
        actorPartner: 'Partner',
        actorSystem: 'System',
        entityTypeLabel: 'Entity type',
        entityLabel: 'Entity',
        actionLabel: 'Action',
        errorTitle: 'The audit trail could not be loaded',
        tableLabel: 'Audit events',
        colWhen: 'When',
        colWho: 'Who',
        colEntityId: 'Entity id',
        colRequest: 'Request',
        colAddress: 'Address',
        colHash: 'Hash',
        emptyTitle: 'No audit events match',
        emptyDescription: 'Widen the date range, or clear the action filter.',
        noteBefore: 'The chain is verified by',
        noteAfter:
          ", not here. This screen shows each event's own hash and the one before it as evidence; it does not claim to have recomputed them.",
      },
      detail: {
        fallbackTitle: 'Audit event',
        breadcrumbEvent: 'Event',
        errorTitle: 'This audit event could not be loaded',
        eventCardTitle: 'Event',
        factOccurred: 'Occurred',
        factRequestId: 'Request id',
        changedTitle: 'What changed',
        beforeTitle: 'Before',
        afterTitle: 'After',
        maskedSubtitle: 'PII masked by the server',
        chainTitle: 'Chain',
        chainSubtitle: 'Verified by fcms verify-integrity, not by this screen',
        prevHash: 'Previous hash',
        selfHash: "This event's hash",
        genesis: 'genesis',
      },
    },

    pack: {
      title: 'Management pack',
      periodLabel: 'Period',
      periodDaily: 'Daily',
      periodWeekly: 'Weekly',
      periodMonthly: 'Monthly',
      endingLabel: 'Ending',
      errorTitle: 'The management pack could not be loaded',
      periodTotalsLabel: 'Period totals',
      tileCollected: 'Collected',
      tilePurchaseValue: 'Purchase value',
      tileBillsIssued: 'Bills issued',
      tilePayouts: 'Payouts',
      tileSessions: 'Sessions',
      tileAverageWait: 'Average queue wait',
      averageWaitHelp:
        'Weighted by the number of tokens each day issued, so a quiet day with one long wait does not dominate.',
      sectionsLabel: 'Management pack sections',
      tabCollection: 'Collection',
      tabBills: 'Bills',
      tabPayouts: 'Payouts',
      tabSportsShare: 'Sports share',
      tabFishMix: 'Fish mix',
      tabQueue: 'Queue',
      tableCollectionLabel: 'Daily collection',
      tableBillsLabel: 'Bills by day',
      tablePayoutsLabel: 'Payouts by day',
      tableSportsLabel: 'Sports share by day',
      tableQueueLabel: 'Queue by day',
      emptyCollection: 'No collection in this period',
      emptyBills: 'No bills in this period',
      emptyPayouts: 'No payouts in this period',
      emptySports: 'No sports landings in this period',
      emptyMix: 'No landings to break down',
      emptyQueue: 'No queue activity in this period',
      mixCardTitle: 'Weight by species',
      mixChartLabel: 'Net weight landed by fish type over the period',
      colDay: 'Day',
      colChannel: 'Channel',
      colSessions: 'Sessions',
      colValue: 'Value',
      colType: 'Type',
      colBillsCount: 'Bills',
      colRuns: 'Runs',
      colPayees: 'Payees',
      colSports: 'Sports',
      colRegular: 'Regular',
      colShare: 'Share',
      colIssued: 'Issued',
      colServed: 'Served',
      colNoShow: 'No-show',
      colAverageWait: 'Average wait',
      colP90: '90th percentile',
    },

    transactions: {
      title: 'Transaction search',
      subtitle: 'Every bill, searchable, from the reporting read models',
      exportsMeta: 'Exports use these filters and cover every matching row, not just this page.',
      anyType: 'Any type',
      anyChannel: 'Any channel',
      anyStatus: 'Any status',
      typeLabel: 'Type',
      channelLabel: 'Channel',
      channelRegular: 'Regular',
      channelSports: 'Sports',
      statusDraft: 'Draft',
      statusFinalized: 'Finalized',
      statusVoided: 'Voided',
      billNoLabel: 'Bill number',
      fishermanHint: 'Leave empty for every fisherman.',
      searchErrorTitle: 'The search did not complete',
      tableLabel: 'Historical bill transactions',
      emptyTitle: 'No transactions match',
      emptyDescription: 'Widen the date range, or clear the bill-number filter.',
      colDay: 'Day',
      colSettledFrom: 'Settled from',
      colSubtotal: 'Subtotal',
      colDiscount: 'Discount',
      colFinalized: 'Finalized',
      thisPage: 'This page',
      chipSelected: 'selected',
      rangeLabel: 'Range',
      exportStartedTitle: 'Export started',
      exportStartedBody: 'The whole filtered set, not just this page.',
      exportErrorTitle: 'The export did not complete',
    },

    vessels: {
      title: 'Vessel activity',
      errorTitle: 'Vessel activity could not be loaded',
      periodTotalsLabel: 'Period totals',
      tileVesselsActive: 'Vessels active',
      tilePurchaseValue: 'Purchase value',
      perVessel: 'Per vessel',
      perVesselPerDay: 'Per vessel per day',
      rollupTableLabel: 'Vessel activity rolled up over the period',
      dailyTableLabel: 'Vessel activity by day',
      emptyTitle: 'No vessel activity in this period',
      showEveryDay: 'Show every day',
      rollUp: 'Roll up per vessel',
      colDay: 'Day',
      colSessions: 'Sessions',
      colValue: 'Value',
      colActiveDays: 'Active days',
    },
  },

  assistant: {
    chat: {
      title: 'Assistant',
      subtitle: 'Answers built only from the reporting views, with their provenance',
      conversationLabel: 'Conversation',
      dailyBrief: 'Daily brief',
      newConversation: 'New conversation',
      conversationTurns: '{{when}} · {{count}} turns',
      alertTitle: 'What this can and cannot see',
      alertBodyBefore: 'The assistant reads the',
      alertBodyAfter:
        'reporting views through registered graph tools, under a database role that holds SELECT on that schema and nothing else. It cannot reach a hot table, it cannot write, and it is filtered to your own site scope. If the graph cannot answer a question it says so and names the nearest metric — it never estimates a financial figure.',
      errorTitle: 'The assistant could not be reached',
      cardTitle: 'Conversation',
      emptyTitle: 'Ask a question',
      emptyDescription: '"How did Kooddoo do this month compared with last month?"',
      roleUser: 'You',
      roleAssistant: 'Assistant',
      warningFlaggedDefault: 'This answer stated a currency figure that appeared in no tool result.',
      warningHitLimit:
        'The tool loop stopped at its bound while the model was still planning. The answer is incomplete.',
      citationsSectionTitle: 'Where these figures came from',
      citationsTableLabel: 'Data the assistant used for this answer',
      colMetric: 'Metric',
      colReadFrom: 'Read from',
      colUnit: 'Unit',
      colRange: 'Range',
      colGroupedBy: 'Grouped by',
      colRows: 'Rows',
      truncatedBadge: 'truncated',
      noCitations: 'This answer cites no tool data. Treat any figure in it with suspicion.',
      toolCallsOne: '{{count}} tool call',
      toolCallsMany: '{{count}} tool calls',
      rowsCount: '{{count}} rows',
      failedBadge: 'failed',
      rowsHash: 'rows sha256 {{hash}}',
      workingLabel: 'Working',
      workingText: 'Working through the graph tools. This runs to completion, not word by word.',
      questionLabel: 'Your question',
      questionPlaceholder: 'How did Kooddoo do this month compared with last month?',
      askButton: 'Ask',
      askErrorTitle: 'The question was not answered',
    },

    /**
     * The chat modal reachable from anywhere in the back office (the launcher
     * in the top bar, or Ctrl K). Separate from `assistant.chat.*` on purpose:
     * that tree belongs to `AssistantView.vue`, the full page, which keeps
     * rendering its own strings. Anything the modal shows and the page does
     * not lives here; anything BOTH show is read from `assistant.chat.*` and
     * is not duplicated into this tree.
     */
    modal: {
      launcherLabel: 'Ask the assistant',
      threadLabel: 'Conversation with the assistant',
      openFullView: 'Open the full assistant page',
      turnsBadge: '{{count}} turns',
      emptyTitle: 'Ask about anything in the reporting views',
      emptyDescription:
        'Every answer names the views and the date ranges it came from. Nothing here is estimated.',
      starterHeading: 'Try one of these',
      starter1: 'How did Kooddoo do this month compared with last month?',
      starter2: 'Which collection point bought the most fish last week?',
      starter3: 'What is the average queue wait at each collection point today?',
      starter4: 'Are any devices offline right now?',
      shortcutHint: 'Tip: press Ctrl K anywhere to open this.',
      thinkingPlanning: 'Planning which metrics to read…',
      thinkingQuerying: 'Reading the reporting views…',
      thinkingComposing: 'Composing the answer…',
      thinkingElapsed: '{{seconds}}s elapsed',
      /**
       * Both forms carry `{{count}}` — the singular deliberately so, since
       * "Read from 1 reporting view" is the sentence, not "Read from a
       * reporting view". `completeness.spec.ts`'s token check depends on it.
       */
      citedFromOne: 'Read from {{count}} reporting view',
      citedFromMany: 'Read from {{count}} reporting views',
      provenanceShow: 'Show where these figures came from',
      provenanceHide: 'Hide where these figures came from',
      colFilters: 'Filtered by',
      colScope: 'Scope',
      /** Unit names for a citation row. `kg` and `laari` are never translated. */
      unitKg: 'kg',
      unitLaari: 'laari',
      unitMinutes: 'minutes',
      unitCount: 'count',
      unitPercent: 'percent',
      disabledTitle: 'The assistant is switched off on this deployment',
      disabledDescription:
        'Everything else in the back office works without it. Ask an administrator to turn on ASSISTANT_ENABLED.',
      providerDownTitle: 'The model could not be reached',
      providerDownBody:
        'The reporting data is fine — the model provider behind the assistant did not answer. Try the question again in a moment.',
      retryQuestion: 'Ask again',
      composerHint: 'Enter sends · Shift + Enter for a new line',
    },

    brief: {
      title: 'Daily brief',
      dayLabel: 'Day',
      composeNow: 'Compose now',
      composedByJobFor: 'Composed by the 07:00 job for {{day}}',
      loadErrorTitle: 'The brief could not be loaded',
      loadingLabel: 'Loading the brief',
      notFoundTitle: 'No brief for that day',
      notFoundDescription: 'The 07:00 job has not run for it, or the day is in the future.',
      composeItNow: 'Compose it now',
      notNarratedTitle: 'The prose was not written by the model',
      notNarratedBody:
        'The model was unavailable when this brief was composed, so the sentences were rendered deterministically from the tool results. The figures are unaffected — they come from the same tools either way.',
      flaggedTitle: 'This brief was flagged',
      flaggedDefaultReason: 'It stated a currency figure that appeared in no tool result.',
      flaggedKeptNote: 'The brief is kept with its reason rather than deleted, so it can be reviewed.',
      headlineFiguresLabel: 'Headline figures',
      briefCardTitle: 'Brief',
      narratedBadge: 'narrated',
      renderedBadge: 'rendered',
      deliveredTo: 'delivered to {{count}}',
      composedStamp: 'Composed {{when}}',
      recomposedStamp: 'last recomposed {{when}}',
      provenanceTitle: 'Provenance',
      provenanceSubtitle: 'The thread this brief was composed in',
      openAssistant: 'Open the assistant',
      provenanceNote:
        'The tool calls behind every figure are recorded on that conversation, with the view read, the row count and a hash of the rows.',
      composedToast: 'Brief composed',
      composeErrorTitle: 'The brief was not composed',
    },
  },
}

export default en
export type MessageTree = typeof en
